/**
 * MonthView component for calendar
 * Displays events in a monthly grid layout
 */

'use client';

import { darkenHexColorWithOpacity } from '@/utils/colorService';
import { Clock, MapPin } from 'lucide-react';
import Link from 'next/link';
import { useMemo } from 'react';
import type { EventTeaser } from '../../types';

interface MonthViewProps {
  events: EventTeaser[];
  currentDate: Date;
  baseUrl?: string;
}

interface DayCell {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  events: EventTeaser[];
}

export function MonthView({ events, currentDate, baseUrl = '/events' }: MonthViewProps) {
  // Build calendar grid
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Calculate start and end of calendar grid (including prev/next month days)
    const startDay = new Date(firstDay);
    startDay.setDate(startDay.getDate() - startDay.getDay()); // Start on Sunday

    const endDay = new Date(lastDay);
    endDay.setDate(endDay.getDate() + (6 - endDay.getDay())); // End on Saturday

    // Build array of days
    const days: DayCell[] = [];
    const currentDay = new Date(startDay);

    while (currentDay <= endDay) {
      const dayDate = new Date(currentDay);
      dayDate.setHours(0, 0, 0, 0);

      // Find events for this day
      const dayEvents = events.filter(event => {
        if (!event.eventDateTimeRange?.start?.timestamp) return false;

        const eventStart = new Date(event.eventDateTimeRange.start.timestamp * 1000);
        eventStart.setHours(0, 0, 0, 0);

        const eventEnd = event.eventDateTimeRange.end?.timestamp
          ? new Date(event.eventDateTimeRange.end.timestamp * 1000)
          : new Date(eventStart);
        eventEnd.setHours(23, 59, 59, 999);

        return dayDate >= eventStart && dayDate <= eventEnd;
      });

      days.push({
        date: new Date(dayDate),
        isCurrentMonth: dayDate.getMonth() === month,
        isToday: dayDate.getTime() === today.getTime(),
        events: dayEvents,
      });

      currentDay.setDate(currentDay.getDate() + 1);
    }

    return days;
  }, [currentDate, events]);

  // Day headers
  const dayHeaders = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="overflow-hidden">
      {/* Day headers */}
      <div className="grid grid-cols-7 border-b bg-muted/50">
        {dayHeaders.map(day => (
          <div
            key={day}
            className="border-r p-2 text-centre text-xs font-medium text-muted-foreground last:border-r-0"
          >
            <span className="hidden sm:inline">{day}</span>
            <span className="sm:hidden">{day[0]}</span>
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7">
        {calendarDays.map((day, index) => {
          return (
            <div
              key={index}
              className={`min-h-[100px] border-b border-r p-2 last:border-r-0 sm:min-h-[120px] ${
                day.isCurrentMonth ? 'bg-background' : 'bg-muted/30'
              } ${day.isToday ? 'ring-2 ring-primary ring-inset' : ''}`}
            >
              {/* Date number */}
              <div className="mb-1 flex items-centre justify-between">
                <span
                  className={`flex h-6 w-6 items-centre justify-centre rounded-full text-sm ${
                    day.isToday
                      ? 'bg-primary font-bold text-primary-foreground'
                      : day.isCurrentMonth
                        ? 'font-medium'
                        : 'text-muted-foreground'
                  }`}
                >
                  {day.date.getDate()}
                </span>
                {day.events.length > 0 && (
                  <span className="rounded-full bg-primary/20 px-1.5 py-0.5 text-xs font-medium text-primary">
                    {day.events.length}
                  </span>
                )}
              </div>

              {/* Events list */}
              <div className="space-y-1">
                {day.events.slice(0, 3).map(event => {
                  const startTime = event.eventDateTimeRange?.start?.timestamp
                    ? new Date(event.eventDateTimeRange.start.timestamp * 1000).toLocaleTimeString(
                        'en-GB',
                        {
                          hour: '2-digit',
                          minute: '2-digit',
                        }
                      )
                    : '';

                  // event.path already contains full path like /events/title
                  const eventLink = event.path;

                  if (!eventLink) return null;

                  // Extract eventTypeColor from eventType
                  const eventTypeObj = event.eventType as
                    | { eventTypeColor?: string | null }
                    | null
                    | undefined;
                  const eventTypeColor = eventTypeObj?.eventTypeColor ?? undefined;

                  return (
                    <Link
                      key={event.id}
                      href={eventLink}
                      className={`group block rounded-md p-1.5 text-xs transition-colours ${
                        eventTypeColor ? '' : 'bg-primary/10 hover:bg-primary/20'
                      }`}
                      style={{
                        backgroundColor: eventTypeColor
                          ? darkenHexColorWithOpacity(eventTypeColor, 80, 0.7)
                          : undefined,
                      }}
                    >
                      <div className="flex items-start gap-1">
                        {startTime && (
                          <Clock
                            className="h-3 w-3 flex-shrink-0 text-muted-foreground"
                            style={{ color: eventTypeColor || undefined }}
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="line-clamp-2 font-medium leading-tight group-hover:text-primary text-white">
                            {event.title}
                          </p>
                          {event.eventLocation && (
                            <div className="mt-0.5 flex items-centre gap-0.5 text-white">
                              <MapPin className="h-2.5 w-2.5 flex-shrink-0" />
                              <span className="truncate text-[10px]">{event.eventLocation}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </Link>
                  );
                })}
                {day.events.length > 3 && (
                  <div className="text-centre text-[10px] text-muted-foreground">
                    +{day.events.length - 3} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
